import React, { useMemo, useRef } from 'react';
import { Button, Typography } from '@getgo/chameleon-web/react';
import { Form, Formik, FormikValues } from 'formik';
import { useTranslation } from 'react-i18next';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useRegistrationContext } from '../../context/registrationContext';
import { useEventContext } from '../../context/eventContext';
import { useHistory } from 'react-router-dom';
import RegFormContainer from './form/regFormContainer';
import {
  createRegistrationSubmissionObj,
  getFormInitialValuesWithQuestionnaireInitialValues,
  getQuestionNameByQuestionDetailsMap,
  getRequiredFieldByDetailsMap,
  getValidationSchemaForRegForm,
  sortedEntries,
  createMixPanelData,
} from './registrationhelper/helper';
import setMixPanelProperties from '../../../mixpanel/setMixPanelProperties';
import {
  onFailureRegisterUserTracker,
  onRegisterUserTracker,
  onSuccessRegisterUserTracker,
} from '../../../mixpanel/Feature/registerUserTracker';
import { setCookie } from '../../utils/browserCookies';
import FormatStringUrl from '../../utils/formatStringUrl';
import API from '../../utils/api';
import { IconButton } from '@getgo/chameleon-web/react';
import { CloseIcon } from '@getgo/chameleon-icons/react';
import '../styles/events.scss';
import EventHostedByDetails from './eventHostedByDetails';
import { FormikHelpers } from 'formik/dist/types';
import '../styles/event-registration-form.scss';

const noOfDefaultQuestion = 3;

interface EventRegistration {
  closeForm(): void;
  openForm(div: HTMLDivElement | null): void;
  isFormVisible: boolean;
}

const EventRegistrationForm = ({ closeForm, isFormVisible, openForm }: EventRegistration) => {
  const isMobOrTabView = window.matchMedia('(max-width: 1024px)').matches;
  const { t } = useTranslation();
  const divRef = useRef<HTMLDivElement>(null);
  const regFormRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { setRegisteredUserDetails } = useRegistrationContext();
  const { eventDetails } = useEventContext();
  const captchaRef = useRef<HCaptcha>(null);

  let initialValues: any = {
    firstName: '',
    lastName: '',
    email: '',
    isDisclaimerChecked: false,
  };
  let fieldNameByDetailsMap: Map<string, any> | null = null;
  let templateQuestionNameByDetailsMap: Map<string, any> | null = null;
  let customQuestionNameByDetailsMap: Map<string, any> | null = null;

  const questionnaireDetails = eventDetails?.questionnaireDetails;
  const questions = questionnaireDetails?.questions;
  const requiredFieldNameByDetailsMap = useMemo(() => getRequiredFieldByDetailsMap(t), [t]);

  if (questionnaireDetails && questions) {
    initialValues = getFormInitialValuesWithQuestionnaireInitialValues(initialValues, questions);

    templateQuestionNameByDetailsMap = getQuestionNameByQuestionDetailsMap(
      questions,
      (question) => question.readOnly,
    );
    customQuestionNameByDetailsMap = getQuestionNameByQuestionDetailsMap(
      questions,
      (question) => !question.readOnly,
      'customQuestion',
    );
  }

  const validationSchemaForRegForm = (() => {
    fieldNameByDetailsMap = new Map([...requiredFieldNameByDetailsMap]);

    if (templateQuestionNameByDetailsMap) {
      fieldNameByDetailsMap = new Map([
        ...fieldNameByDetailsMap,
        ...templateQuestionNameByDetailsMap,
      ]);
    }

    if (customQuestionNameByDetailsMap) {
      fieldNameByDetailsMap = new Map([
        ...fieldNameByDetailsMap,
        ...customQuestionNameByDetailsMap,
      ]);
    }

    return getValidationSchemaForRegForm(fieldNameByDetailsMap, questionnaireDetails);
  })();

  const openMobForm = (actions: FormikHelpers<any>) => {
    openForm(regFormRef.current);
    actions.setTouched({});
  };

  const triggerCaptcha = async (values: FormikValues, actions: FormikHelpers<any>) => {
    if (!isFormVisible) {
      return;
    }
    actions.setSubmitting(true);
    if (values.humanRegistrantToken) return await registerAttendee(values, actions);

    const { response } = (await captchaRef?.current?.execute({ async: true })) ?? {};
    if (response) await registerAttendee({ ...values, humanRegistrantToken: response }, actions);
    actions.setSubmitting(false);
  };

  const registerAttendee = async (values: FormikValues, actions: FormikHelpers<any>) => {
    const eventKey = eventDetails?.eventKey;

    const userDetails: any = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
    };

    onRegisterUserTracker({
      eventKey: eventDetails?.eventKey,
      userDetails: values,
      description: 'Registering For The Event',
    });
    const registrationSubmissionReq = createRegistrationSubmissionObj(
      fieldNameByDetailsMap,
      questionnaireDetails,
      values,
    );
    const mixpanelData = createMixPanelData(questionnaireDetails, values, fieldNameByDetailsMap);
    let totalNoOfQuestion = noOfDefaultQuestion;
    let totalnoOfTemplateQuestion = 0;
    let totalnoOfCustomQuestion = 0;
    let noOfNonMandatoryQuestion: any = [];
    let noOfNonMandatoryQuestionAnswered = [];
    let noOfNonMandatoryQuestionAnsweredInPercent = 0;

    if (questionnaireDetails && questionnaireDetails.questions) {
      totalNoOfQuestion = totalNoOfQuestion + questionnaireDetails?.questions.length;
      totalnoOfTemplateQuestion = questionnaireDetails?.questions.filter(
        (question) => question.readOnly,
      ).length;
      totalnoOfCustomQuestion = questionnaireDetails?.questions.filter(
        (question) => !question.readOnly,
      ).length;
      noOfNonMandatoryQuestion = questionnaireDetails?.questions.filter(
        (question) => !question.required,
      );
      noOfNonMandatoryQuestionAnswered = registrationSubmissionReq.questionnaireSubmission.answers.filter(
        (answer: any) =>
          noOfNonMandatoryQuestion &&
          noOfNonMandatoryQuestion?.findIndex(
            (question: any) => question.questionKey === answer.questionKey,
          ) > -1,
      );
      noOfNonMandatoryQuestionAnsweredInPercent =
        noOfNonMandatoryQuestion &&
        noOfNonMandatoryQuestion.length &&
        (noOfNonMandatoryQuestionAnswered.length / noOfNonMandatoryQuestion.length) * 100;
    }

    try {
      const response = await API.sendRegistrationDetails(eventKey, registrationSubmissionReq);

      if (response.status === 201) {
        const { userKey, eventRegistrationId } = response.data;
        userDetails['userKey'] = userKey;
        userDetails['eventRegistrationId'] = eventRegistrationId;
        setRegisteredUserDetails?.(userDetails);

        setMixPanelProperties(userDetails, eventKey, eventRegistrationId);

        onSuccessRegisterUserTracker(
          {
            eventKey,
            userDetails: mixpanelData,
          },
          noOfNonMandatoryQuestion?.length,
          noOfNonMandatoryQuestionAnswered.length,
          noOfNonMandatoryQuestionAnsweredInPercent,
          totalNoOfQuestion,
          totalnoOfTemplateQuestion,
          totalnoOfCustomQuestion,
          'Succesfully Registered For The Event',
        );

        setCookie(eventKey, JSON.stringify(userDetails), eventDetails?.end, 7);
        captchaRef?.current?.resetCaptcha();
        actions.setSubmitting(false);
        closeForm();
        history.push(`/successregistration/${eventKey}`);
      }
    } catch (e) {
      console.log(e);
      onFailureRegisterUserTracker(
        {
          eventKey,
          userDetails: mixpanelData,
        },
        noOfNonMandatoryQuestion?.length,
        noOfNonMandatoryQuestionAnswered.length,
        noOfNonMandatoryQuestionAnsweredInPercent,
        totalNoOfQuestion,
        totalnoOfTemplateQuestion,
        totalnoOfCustomQuestion,
        'Failed To Register For The Event',
      );
      captchaRef?.current?.resetCaptcha();
      actions.setSubmitting(false);
      onFailureRegisterUserTracker({
        eventKey: eventDetails?.eventKey,
        userDetails: values,
        description: 'Failed To Register For The Event',
      });
    }
  };

  const totalFields = fieldNameByDetailsMap?.size;
  return (
    <>
      {isFormVisible && <div className="overlay" onClick={closeForm}></div>}
      <div className={isFormVisible ? 'event-registration-form' : ''} ref={divRef}>
        <Formik
          initialValues={initialValues}
          onSubmit={triggerCaptcha}
          validationSchema={isFormVisible && validationSchemaForRegForm}
        >
          {(formikProps) => {
            return (
              <Form>
                <div
                  className={`event-registration-form-fixed ${
                    isFormVisible ? 'open-modal' : 'close-modal'
                  }`}
                >
                  <div className="registrationHeader">
                    <Typography variant="heading-small" color="text-01" className="form-heading">
                      {t('event.registration.title')}
                    </Typography>
                    {isMobOrTabView && (
                      <IconButton variant="neutral" onClick={closeForm}>
                        <CloseIcon />
                      </IconButton>
                    )}
                  </div>
                  <div className="registrationForm" ref={regFormRef}>
                    {requiredFieldNameByDetailsMap &&
                      Array.from(requiredFieldNameByDetailsMap).map(([name, details]) => {
                        const { ...restDetails } = details;
                        return (
                          <RegFormContainer
                            key={name}
                            name={name}
                            placeholder={t(`event.form.${name}Placeholder`)}
                            noOfFields={totalFields}
                            {...restDetails}
                          />
                        );
                      })}
                    {templateQuestionNameByDetailsMap &&
                      sortedEntries(templateQuestionNameByDetailsMap).map(([name, details]) => {
                        const {
                          questionKey, // eslint-disable-line @typescript-eslint/no-unused-vars
                          sortOrder, // eslint-disable-line @typescript-eslint/no-unused-vars
                          questionType, // eslint-disable-line @typescript-eslint/no-unused-vars
                          isTemplateQuestion, // eslint-disable-line @typescript-eslint/no-unused-vars
                          ...restDetails
                        } = details;
                        return (
                          <RegFormContainer
                            key={name}
                            name={name}
                            noOfFields={totalFields}
                            placeholder={t(`event.form.${name}Placeholder`)}
                            {...restDetails}
                          />
                        );
                      })}
                    {customQuestionNameByDetailsMap &&
                      sortedEntries(customQuestionNameByDetailsMap).map(([name, details]) => {
                        const {
                          questionKey, // eslint-disable-line @typescript-eslint/no-unused-vars
                          sortOrder, // eslint-disable-line @typescript-eslint/no-unused-vars
                          questionType, // eslint-disable-line @typescript-eslint/no-unused-vars
                          isTemplateQuestion, // eslint-disable-line @typescript-eslint/no-unused-vars
                          ...restDetails
                        } = details;
                        return (
                          <RegFormContainer
                            key={name}
                            name={name}
                            noOfFields={totalFields}
                            placeholder={t(`event.form.customQuestionPlaceholder`)}
                            {...restDetails}
                          />
                        );
                      })}

                    {questionnaireDetails?.customDisclaimerEnabled ? (
                      <RegFormContainer name="isDisclaimerChecked" controlType="Checkbox">
                        <FormatStringUrl>{questionnaireDetails.disclaimerText}</FormatStringUrl>
                        <Typography className="inline-block" color="text-02">
                          {t('event.form.required')}
                        </Typography>
                      </RegFormContainer>
                    ) : (
                      <Typography className="text-disclaimer" color="text-02">
                        {t('event.form.disclaimerCheckboxLabel')}
                      </Typography>
                    )}

                    <div className="h-captcha">
                      <HCaptcha
                        size="invisible"
                        onVerify={(token) =>
                          formikProps.setFieldValue('humanRegistrantToken', token)
                        }
                        sitekey={process.env.REACT_APP_HCAPTCHA_KEY as string}
                        onExpire={() => formikProps.setFieldValue('humanRegistrantToken', null)}
                        id="h-captcha-input"
                        ref={captchaRef}
                      />
                    </div>

                    {isMobOrTabView && eventDetails?.organizers?.length && (
                      <EventHostedByDetails eventDetail={eventDetails} />
                    )}
                  </div>
                </div>
                <div className="register-button-background">
                  {isFormVisible ? (
                    <Button
                      size="large"
                      type="submit"
                      disabled={!formikProps.isValid || formikProps.isSubmitting}
                      fullwidth={isMobOrTabView}
                    >
                      {t('event.form.registerToAttend')}
                    </Button>
                  ) : (
                    <Button size="large" onClick={() => openMobForm(formikProps)} fullwidth>
                      {t('event.form.registerToAttend')}
                    </Button>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default EventRegistrationForm;
